import { AddSharp, ArrowBack, ArrowForwardIosSharp } from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    Grid,
    styled,
    Typography,
} from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import ApplicationPermission from "models/Permission/ApplicationPermission";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "shared/hooks/useForm";
import { AuthStore } from "stores/AuthStore";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import useOrganization from "../hooks/useOrganization";
import {
    Organization,
    Saml2BindingType,
} from "../types/Organization.type";
import IntegrationSettings from "./OrganizationDetails/IntegrationConfiguration/IntegrationSettings";
import MetadataConfigurationWrapper from "./OrganizationDetails/MetadataFileConfiguration/MetadataConfigurationWrapper";
import OrganizationDetails from "./OrganizationDetails/OrganizationDetails";
import ServiceSetup from "./OrganizationDetails/ServiceSetup";
import {
    validateKboUserAgreementContent,
    validateKboUserAgreementEffectiveOn,
    validateKboUserAgreementVersion,
} from "../kboUserAgreementConfiguration";
import { AcxAgentService } from "services/AcxAgentService";
import AcxDateRangeInput from "components/UI/Calendar/DateRange/AcxDateRangeInput";
import { DatePickerComponentStore } from "stores/ComponentStores/DatePickerComponentStore";

function downloadFromBlob(blob: Blob, name?: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.href = url;
    if (name) a.download = name;
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

const createContainerName = (name: string) => {
    return name
        .replace(/[^\w\s-]/g, "")
        .toLowerCase()
        .replace(/ /g, "-")
        .replace(/-+/g, "-")
        .substring(0, 63);
};

const defaultValues: Organization = {
    name: "",
    databaseConnectionString: "",
    pgBouncerConnectionString: "",
    qbAppId: "",
    blobContainer: "",
    pgpKeyName: "",
    dataProcessingOptions: [],
    isActive: true,
    enhancedEvalStatus: false,
    serviceSetup: [],
};

const EditPermissionCheckboxDiv = styled("div")(() => ({
    flex: "1",
    justifyContent: "center",
    display: "flex",
}));

const EditPermissionExpandChildrenDiv = styled("div")(() => ({
    display: "flex",
    justifyContent: "left",
    marginLeft: "12px",
    "& .MuiSvgIcon-root.Mui-expanded": {
        transform: "rotate(90deg)",
    },
    alignItems: "center",
}));

interface IOrganizationDetailProps {
    id?: string;
}

const OrganizationDetail: React.FC<
    React.PropsWithChildren<IOrganizationDetailProps>
> = ({ id }) => {
    const navigate = useNavigate();

    const authStore = useStore(AuthStore);
    const permStore = authStore.permStore;
    const messageStore = useStore(MessageStore);
    const orgStore = useStore(AuthStore).orgStore;

    const [kboDataOpen, setKboDataOpen] = useState(false);
    const [kboDataLoading, setKboDataLoading] = useState(false);
    const kboDataDatePickerStore = useMemo(
        () => new DatePickerComponentStore(),
        [],
    );

    const [validation, setValidation] = React.useState({});
    const [confirm, setConfirm] = React.useState(false);
    const [currentOrgId, setCurrentOrgId] = React.useState<string>();

    //Org Permissions Stuff
    const [permissionEditMode, setPermissionEditMode] = React.useState(false);
    //List of all possible permissions an organization could have
    const [possiblePermissions, setPossiblePermissions] =
        React.useState<ApplicationPermission[]>();
    //List for UI to keep track of which parent permissions are expanded
    const [expandedResources, setExpandedResources] =
        React.useState<string[]>();
    //List of enabled permissions, that updates as UI updates
    const [organizationPermissions, setOrganizationPermissions] =
        React.useState<ApplicationPermission[]>();

    const {
        value,
        registerSubmit,
        errors,
        onChange,
        isSubmitting,
        setValue,
        reset,
    } = useForm<Organization>(defaultValues, validation);

    const {
        organization,
        saveOrganization,
        isLoading,
        checkDbConnection,
    } = useOrganization({
        id,
    });

    const updateOrg = (
        val: string | string[] | boolean | number | Saml2BindingType,
        prop: keyof Organization,
    ) => {
        onChange(prop, val);
    };

    const save = () => {
        if ((value?.serviceSetup?.length || 0) > 5) {
            setConfirm(true);
        } else {
            return confirmSave();
        }
    };

    const confirmSave = () => {
        setConfirm(false);
        return saveOrganization(value || {});
    };

    const onClose = () => {
        setConfirm(false);
    };

    const onSuccess = (res: Organization) => {
        const connectionString = value?.databaseConnectionString;
        const databaseVersion = Number(value?.databaseVersion);

        if (
            (connectionString && connectionString.trim() !== "") ||
            !isNaN(databaseVersion)
        ) {
            if (connectionString) {
                checkDbConnection(connectionString, databaseVersion);
            } else {
                checkDbConnection("", databaseVersion);
            }
        } else {
            messageStore.logMessage(
                `Successfully saved organization`,
                "success",
            );
        }

        //This needs to be updated in the future to ensure synchronicity between database and stores on all organization properties
        if (orgStore.selectedOrganization !== null) {
            orgStore.selectedOrganization!.enhancedEvalStatus =
                res.enhancedEvalStatus;
            orgStore.selectedOrganization!.enhancedPermissionStatus =
                res.enhancedPermissionStatus;
        }

        if (!id && res?.id) {
            navigate(`/admin/organizations/${res.id}`);
        }
    };

    const onFail = (message: string) => {
        messageStore.logMessage(
            `Failed to save organization${message ? `: ${message}` : ""}`,
            "error",
        );
    };

    const updateContainerName = (val?: string | number) => {
        if (val) {
            if (!id && !value?.blobContainer) {
                onChange("blobContainer", createContainerName(String(val)));
            }
        } else {
            onChange("blobContainer", createContainerName(""));
        }
    };

    React.useEffect(() => {
        if (organization) {
            setValue(organization, false);
        }
    }, [organization, setValue]);

    React.useEffect(() => {
        if (currentOrgId !== id) {
            reset();
            setCurrentOrgId(id);
        }
    }, [currentOrgId, id, reset]);

    React.useEffect(() => {
        setValidation((prev) => ({
            ...prev,
            blobContainer: id
                ? {}
                : {
                      required: {
                          value: true,
                          message: "Blob container is required.",
                      },
                      minLength: {
                          value: 3,
                          message: "Must be at least 3 characters.",
                      },
                      maxLength: {
                          value: 63,
                          message: "Must be at most 63 characters.",
                      },
                      pattern: [
                          {
                              value: /^[a-z0-9]/,
                              message:
                                  "Must start with a lowercase letter or number.",
                          },
                          {
                              value: /\w*[a-z]\w*/,
                              message:
                                  "Must contain at least one lowercase letter.",
                          },
                          {
                              value: /^.*[^-_]$/,
                              message: "Cannot start or end with a hyphen.",
                          },
                          {
                              value: /[A-Z]/,
                              opposite: true,
                              message: "Uppercase letters are not allowed.",
                          },
                          {
                              value: /^[^-]+(?!.*--).+[^-]+$/,
                              message: "Consecutive hyphens are not allowed.",
                          },
                      ],
                  },
            requireMfa: {
                function: {
                    value: (values: Organization) =>
                        values.requireMfa &&
                        !values.mfaAuthenticatorAppEnabled &&
                        !values.mfaEmailEnabled
                            ? "Select at least one MFA method."
                            : undefined,
                },
            },
            // Only ultra users see these fields, so nobody else is held up by them.
            ...(authStore.isUserUltra() && {
                kboUserAgreementVersion: {
                    function: { value: validateKboUserAgreementVersion },
                },
                kboUserAgreementEffectiveOn: {
                    function: { value: validateKboUserAgreementEffectiveOn },
                },
                kboUserAgreementContent: {
                    function: { value: validateKboUserAgreementContent },
                },
            }),
        }));
    }, [authStore, id]);

    let resources;

    if (!organizationPermissions) {
        const getOrgPermissions = async () => {
            if (organization?.id) {
                const perms = await permStore.getOrgPermissions(
                    organization?.id,
                );
                setOrganizationPermissions(perms);
            }
        };
        getOrgPermissions();
    }

    //Properly updates the list of final org permissions
    const updateOrgPermissions = (e, permission: ApplicationPermission) => {
        e.stopPropagation();

        const permissionsToChange: ApplicationPermission[] = [permission];

        const addPermissionEvaluateParents = (
            permission: ApplicationPermission,
        ) => {
            //If the permission has a parent and that parent isn't in the list, add it to the permissions to update.
            if (
                permission.parentPermission &&
                (organizationPermissions?.filter(
                    (perm) => perm.id === permission.parentPermission,
                ).length ?? 0) < 1
            ) {
                permissionsToChange.push(
                    possiblePermissions?.find(
                        (perm) => perm.id === permission.parentPermission,
                    ) ?? new ApplicationPermission(),
                );

                //If the parent has a parent run recursively
                if (
                    possiblePermissions?.find(
                        (perm) => perm.id === permission.parentPermission,
                    )?.parentPermission
                ) {
                    addPermissionEvaluateParents(
                        possiblePermissions?.find(
                            (perm) => perm.id === permission.parentPermission,
                        ) ?? new ApplicationPermission(),
                    );
                }
            }
        };

        const removePermissionEvaluateParent = (
            permission: ApplicationPermission,
        ) => {
            //If the permission has a parent and it's the only one in the list with that parent, add it to the permissions to update
            if (
                permission.parentPermission &&
                (organizationPermissions?.filter(
                    (perm) => perm.id === permission.parentPermission,
                ).length ?? 0) > 0 &&
                organizationPermissions?.filter(
                    (perm) =>
                        perm.parentPermission === permission.parentPermission,
                ).length === 1
            ) {
                permissionsToChange.push(
                    organizationPermissions?.find(
                        (perm) => perm.id === permission.parentPermission,
                    ) ?? new ApplicationPermission(),
                );

                //If the parent has a parent run recursively
                if (
                    possiblePermissions?.find(
                        (perm) => perm.id === permission.parentPermission,
                    )?.parentPermission
                ) {
                    removePermissionEvaluateParent(
                        possiblePermissions?.find(
                            (perm) => perm.id === permission.parentPermission,
                        ) ?? new ApplicationPermission(),
                    );
                }
            }
        };

        if (
            organizationPermissions?.filter((perm) => perm.id === permission.id)
                .length ??
            -1 > 0
        ) {
            if (permission.action === "Edit") {
                const viewPermission = possiblePermissions?.find(
                    (perm) =>
                        perm.resource === permission.resource &&
                        perm.action === "View",
                );
                if (viewPermission) {
                    permissionsToChange.push(viewPermission);
                    removePermissionEvaluateParent(viewPermission);
                }
            }
            removePermissionEvaluateParent(permission);
            setOrganizationPermissions(
                organizationPermissions?.filter(
                    (perm) =>
                        !(
                            permissionsToChange.filter((p) => p.id === perm.id)
                                .length > 0
                        ),
                ),
            );
        } else {
            if (permission.action === "Edit") {
                const viewPermission = possiblePermissions?.find(
                    (perm) =>
                        perm.resource === permission.resource &&
                        perm.action === "View",
                );
                if (
                    viewPermission &&
                    !organizationPermissions?.includes(viewPermission)
                ) {
                    permissionsToChange.push(viewPermission);
                    addPermissionEvaluateParents(viewPermission);
                }
            }
            addPermissionEvaluateParents(permission);
            setOrganizationPermissions(
                organizationPermissions?.concat(permissionsToChange),
            );
        }
    };

    if (possiblePermissions && possiblePermissions.length > 0) {
        // Recursive Permission Builder
        const createNestedPermissions = (resource: string) => {
            let permissions = possiblePermissions.filter(
                (perm) => perm.resource === resource,
            )!;
            let children = possiblePermissions.filter((perm) =>
                permissions
                    .flatMap((perm) => perm.id)
                    .includes(perm.parentPermission),
            );
            //If the resource has children, the resource needs to be an expanding dropdown, who children call this function recursively
            if (children && children.length > 0) {
                return (
                    <li key={resource}>
                        <Accordion
                            expanded={
                                expandedResources?.includes(resource) ?? false
                            }
                            onChange={() =>
                                expandedResources?.includes(resource)
                                    ? setExpandedResources(
                                          expandedResources.filter(
                                              (res) => res !== resource,
                                          ),
                                      )
                                    : setExpandedResources(
                                          expandedResources?.concat([
                                              resource,
                                          ]) ?? [resource],
                                      )
                            }
                        >
                            <AccordionSummary
                                sx={{
                                    padding: "0px",
                                    flexDirection: "row-reverse",
                                    alignItems: "center",
                                    boxShadow:
                                        "0px 2px 1px -1px rgb(10 10 10 / 20%), 0px 1px 1px 0px rgb(10 10 10 / 14%), 0px 1px 3px 0px rgb(10 10 10 / 12%)",
                                    //borderBottom: "1px solid #000000"
                                }}
                            >
                                <div
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <EditPermissionExpandChildrenDiv>
                                        <ArrowForwardIosSharp
                                            className={
                                                expandedResources?.includes(
                                                    resource,
                                                )
                                                    ? "Mui-expanded"
                                                    : ""
                                            }
                                            style={{ fontSize: "0.9rem" }}
                                        />
                                    </EditPermissionExpandChildrenDiv>
                                    <Typography
                                        sx={{
                                            display: "flex",
                                            flex: "1",
                                            justifyContent: "center",
                                            marginRight: "26px",
                                        }}
                                    >
                                        {resource}
                                    </Typography>
                                </div>

                                <div
                                    style={{
                                        marginLeft: "auto",
                                        justifyContent: "space-around",
                                        display: "flex",
                                        minWidth: "250px",
                                    }}
                                >
                                    {/* <EditPermissionCheckboxDiv>
                                        {possiblePermissions.filter(perm => perm.action === "View" && perm.resource === resource).length > 0 &&
                                            <Checkbox checked={!!(organizationPermissions?.find(perm => perm.action === "View" && perm.resource === resource))} color="primary" onClick={e => updateOrgPermissions(e, permissions.find(perm => perm.action === "View") ?? new ApplicationPermission())} />
                                        }
                                    </EditPermissionCheckboxDiv>
                                    <EditPermissionCheckboxDiv>
                                        {possiblePermissions.filter(perm => perm.action === "Edit" && perm.resource === resource).length > 0 &&
                                            <Checkbox checked={!!(organizationPermissions?.find(perm => perm.action === "Edit" && perm.resource === resource))} color="primary" onClick={e => updateOrgPermissions(e, permissions.find(perm => perm.action === "Edit") ?? new ApplicationPermission())} />
                                        }
                                    </EditPermissionCheckboxDiv> */}
                                </div>
                            </AccordionSummary>
                            <AccordionDetails style={{ padding: "0px" }}>
                                <ul
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        backgroundColor: "#FFFFFF",
                                        listStyle: "none",
                                        margin: "0px",
                                        padding: "0px",
                                        minWidth: "100%",
                                    }}
                                >
                                    {Array.from(
                                        new Set(
                                            children.flatMap(
                                                (child) => child.resource,
                                            ),
                                        ),
                                    ).map((child) =>
                                        createNestedPermissions(child),
                                    )}
                                </ul>
                            </AccordionDetails>
                        </Accordion>
                    </li>
                );
            } else {
                //If the resource has no children (meaning it is probably a child), go ahead and return it.
                return (
                    <li
                        key={resource}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            minHeight: "50px",
                            borderBottom: "1px solid #E5E5E5",
                        }}
                    >
                        <div style={{ flex: "1 1 0px", textAlign: "center" }}>
                            {resource}
                        </div>
                        <div
                            style={{
                                marginLeft: "auto",
                                justifyContent: "space-around",
                                display: "flex",
                                minWidth: "250px",
                            }}
                        >
                            <EditPermissionCheckboxDiv>
                                {possiblePermissions.filter(
                                    (perm) =>
                                        perm.action === "View" &&
                                        perm.resource === resource,
                                ).length > 0 && (
                                    <Checkbox
                                        checked={
                                            !!organizationPermissions?.find(
                                                (perm) =>
                                                    perm.action === "View" &&
                                                    perm.resource === resource,
                                            ) ||
                                            !!organizationPermissions?.find(
                                                (perm) =>
                                                    perm.action === "Edit" &&
                                                    perm.resource === resource,
                                            )
                                        }
                                        disabled={
                                            !!organizationPermissions?.find(
                                                (perm) =>
                                                    perm.action === "Edit" &&
                                                    perm.resource === resource,
                                            )
                                        }
                                        color="primary"
                                        onClick={(e) =>
                                            updateOrgPermissions(
                                                e,
                                                permissions.find(
                                                    (perm) =>
                                                        perm.action === "View",
                                                ) ??
                                                    new ApplicationPermission(),
                                            )
                                        }
                                    />
                                )}
                            </EditPermissionCheckboxDiv>
                            <EditPermissionCheckboxDiv>
                                {possiblePermissions.filter(
                                    (perm) =>
                                        perm.action === "Edit" &&
                                        perm.resource === resource,
                                ).length > 0 && (
                                    <Checkbox
                                        checked={
                                            !!organizationPermissions?.find(
                                                (perm) =>
                                                    perm.action === "Edit" &&
                                                    perm.resource === resource,
                                            )
                                        }
                                        color="primary"
                                        onClick={(e) =>
                                            updateOrgPermissions(
                                                e,
                                                permissions.find(
                                                    (perm) =>
                                                        perm.action === "Edit",
                                                ) ??
                                                    new ApplicationPermission(),
                                            )
                                        }
                                    />
                                )}
                            </EditPermissionCheckboxDiv>
                        </div>
                    </li>
                );
            }
        };

        //Uniquely iterate through resources, we will consider actions later on in the checkbox UI
        resources = Array.from(
            new Set(possiblePermissions.flatMap((perm) => perm.resource)),
        ).map((resource) => {
            let permissions = possiblePermissions.filter(
                (perm) => perm.resource === resource,
            )!;
            let children = possiblePermissions.filter((perm) =>
                permissions
                    .flatMap((perm) => perm.id)
                    .includes(perm.parentPermission),
            );
            //If the resource has a parent, it will be rendered later on in the loop
            if (!permissions.find((perm) => perm.parentPermission)) {
                //If the resource has children, it will call this helper function that creates it, and it's children
                if (children && children.length > 0) {
                    return createNestedPermissions(resource);
                } else {
                    //If the resource has no children and no parent, go ahead and return it
                    return (
                        <li
                            key={resource}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                minHeight: "50px",
                                borderBottom: "1px solid #E5E5E5",
                            }}
                        >
                            <div
                                style={{ flex: "1 1 0px", textAlign: "center" }}
                            >
                                {resource}
                            </div>
                            <div
                                style={{
                                    marginLeft: "auto",
                                    justifyContent: "space-around",
                                    display: "flex",
                                    minWidth: "250px",
                                }}
                            >
                                <EditPermissionCheckboxDiv>
                                    {possiblePermissions.filter(
                                        (perm) =>
                                            perm.action === "View" &&
                                            perm.resource === resource,
                                    ).length > 0 && (
                                        <Checkbox
                                            checked={
                                                !!organizationPermissions?.find(
                                                    (perm) =>
                                                        perm.action ===
                                                            "View" &&
                                                        perm.resource ===
                                                            resource,
                                                ) ||
                                                !!organizationPermissions?.find(
                                                    (perm) =>
                                                        perm.action ===
                                                            "Edit" &&
                                                        perm.resource ===
                                                            resource,
                                                )
                                            }
                                            disabled={
                                                !!organizationPermissions?.find(
                                                    (perm) =>
                                                        perm.action ===
                                                            "Edit" &&
                                                        perm.resource ===
                                                            resource,
                                                )
                                            }
                                            color="primary"
                                            onClick={(e) =>
                                                updateOrgPermissions(
                                                    e,
                                                    permissions.find(
                                                        (perm) =>
                                                            perm.action ===
                                                            "View",
                                                    ) ??
                                                        new ApplicationPermission(),
                                                )
                                            }
                                        />
                                    )}
                                </EditPermissionCheckboxDiv>
                                <EditPermissionCheckboxDiv>
                                    {possiblePermissions.filter(
                                        (perm) =>
                                            perm.action === "Edit" &&
                                            perm.resource === resource,
                                    ).length > 0 && (
                                        <Checkbox
                                            checked={
                                                !!organizationPermissions?.find(
                                                    (perm) =>
                                                        perm.action ===
                                                            "Edit" &&
                                                        perm.resource ===
                                                            resource,
                                                )
                                            }
                                            color="primary"
                                            onClick={(e) =>
                                                updateOrgPermissions(
                                                    e,
                                                    permissions.find(
                                                        (perm) =>
                                                            perm.action ===
                                                            "Edit",
                                                    ) ??
                                                        new ApplicationPermission(),
                                                )
                                            }
                                        />
                                    )}
                                </EditPermissionCheckboxDiv>
                            </div>
                        </li>
                    );
                }
            }
            return <li key={resource}></li>;
        });
    } else if (possiblePermissions === undefined) {
        const getPermissions = async () => {
            if (organization?.id) {
                const perms = await permStore.getAllPermissions(
                    organization?.id,
                );
                setPossiblePermissions(perms);
            }
        };
        getPermissions();
    }

    const exitPermissionMode = () => {
        setOrganizationPermissions(undefined);
        setPermissionEditMode(false);
    };

    const saveOrgPermissions = async () => {
        try {
            const numberPermissionsUpdated =
                await permStore.updateOrgPermissions(
                    organization?.id ?? "",
                    organizationPermissions ?? [],
                );
            messageStore.logMessage(
                `Successfully updated ${numberPermissionsUpdated} permission(s)`,
                "success",
            );
        } catch {
            messageStore.logMessage(
                `Error updating permission(s). Please try again.`,
                "error",
            );
        }
    };

    const handleAvaDataDownload = React.useCallback(async () => {
        try {
            setKboDataLoading(true);
            const startDate = kboDataDatePickerStore.beginDate;
            const endDate = kboDataDatePickerStore.endDate;
            const response = await new AcxAgentService().downloadChats(
                startDate?.toISOString(),
                endDate?.toISOString(),
            );
            const blob = await response.blob();

            if (blob.size === 0)
                messageStore.logInfo(
                    "No KBo chat data for the specified date range.",
                );
            else downloadFromBlob(blob, "kbo.csv");
            setKboDataOpen(false);
        } catch (err: unknown) {
            messageStore.logError(JSON.stringify(err));
        } finally {
            setKboDataLoading(false);
        }
    }, [
        kboDataDatePickerStore.beginDate,
        kboDataDatePickerStore.endDate,
        messageStore,
    ]);

    return (
        <>
            <div
                style={{
                    width: "80%",
                    marginLeft: "auto",
                    marginRight: "auto",
                    paddingTop: 20,
                    paddingBottom: 20,
                }}
            >
                {!permissionEditMode ? (
                    <>
                        <Grid
                            style={{
                                marginTop: theme.spacing(1),
                                marginBottom: theme.spacing(1),
                            }}
                            container
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Grid item>
                                <Typography variant="subtitle1">
                                    Org Details
                                </Typography>
                            </Grid>

                            <div
                                style={{
                                    marginLeft: "auto",
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "10px",
                                }}
                            >
                                {authStore.isUserUltra() && !!organization && (
                                    <AcxButton
                                        color="primary"
                                        startIcon={<AddSharp />}
                                        onClick={() => {
                                            navigate(
                                                "/admin/organizations/add",
                                            );
                                        }}
                                    >
                                        Create New Organization
                                    </AcxButton>
                                )}

                                {authStore.canUserView("KBo Chat Data") &&
                                    !!organization && (
                                        <Grid item>
                                            <AcxButton
                                                fullWidth={false}
                                                onClick={() =>
                                                    setKboDataOpen(true)
                                                }
                                            >
                                                KBo Data
                                            </AcxButton>
                                            <AcxDialog
                                                isOpen={kboDataOpen}
                                                onClose={() =>
                                                    setKboDataOpen(false)
                                                }
                                                title="Export KBo Chat Data"
                                                dialogContentChildren={
                                                    <Grid container>
                                                        <Grid item>
                                                            <AcxDateRangeInput
                                                                hideLabel
                                                                sideBar
                                                                doubleCalendar
                                                                maxWidth="none"
                                                                datePickerStore={
                                                                    kboDataDatePickerStore
                                                                }
                                                                defaultStartDate={kboDataDatePickerStore.beginDate.startOf(
                                                                    "day",
                                                                )}
                                                                defaultEndDate={
                                                                    kboDataDatePickerStore.endDate
                                                                }
                                                                onSelect={(
                                                                    start,
                                                                    end,
                                                                ) => {
                                                                    kboDataDatePickerStore.setBeginDate(
                                                                        start,
                                                                    );
                                                                    kboDataDatePickerStore.setEndDate(
                                                                        end,
                                                                    );
                                                                }}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                }
                                            >
                                                <AcxButton
                                                    fullWidth={false}
                                                    loading={kboDataLoading}
                                                    onClick={
                                                        handleAvaDataDownload
                                                    }
                                                >
                                                    Download
                                                </AcxButton>
                                            </AcxDialog>
                                        </Grid>
                                    )}

                                {authStore.isUserUltra() &&
                                    organization?.enhancedPermissionStatus && (
                                        <Grid item>
                                            <AcxButton
                                                fullWidth
                                                onClick={() =>
                                                    setPermissionEditMode(true)
                                                }
                                                color="primary"
                                                disabled={isSubmitting}
                                            >
                                                Edit Organization Permissions
                                            </AcxButton>
                                        </Grid>
                                    )}

                                {!organization && !isLoading && (
                                    <Grid item>
                                        <AcxButton
                                            onClick={() => navigate(-1)}
                                            color="primary"
                                            // loading={isLoading || isSubmitting}
                                            // buttonDisabled={isSubmitting}
                                        >
                                            Cancel
                                        </AcxButton>
                                    </Grid>
                                )}

                                {authStore.canUserEdit(
                                    "Organization Details",
                                ) && (
                                    <Grid item>
                                        <AcxButton
                                            fullWidth
                                            onClick={registerSubmit(save, {
                                                onSuccess,
                                                onFail,
                                            })}
                                            color="primary"
                                            loading={isLoading || isSubmitting}
                                            disabled={isSubmitting}
                                        >
                                            Save Changes
                                        </AcxButton>
                                    </Grid>
                                )}
                            </div>
                        </Grid>

                        {authStore.canUserEdit("Organization Details") && (
                            <OrganizationDetails
                                updateOrg={updateOrg}
                                isLoading={isLoading}
                                org={value}
                                fieldErrors={errors?.fieldErrors}
                                formErrors={errors?.form}
                                updateContainerName={updateContainerName}
                            />
                        )}

                        {authStore.canUserEdit("Organization Details") &&
                            !!id && <MetadataConfigurationWrapper orgId={id} />}

                        {authStore.canUserEdit("Organization Details") && (
                            <IntegrationSettings orgId={id} />
                        )}

                        <ServiceSetup
                            updateOrg={updateOrg}
                            setup={value?.serviceSetup}
                            fieldErrors={errors?.fieldErrors}
                            disabled={
                                !authStore.canUserEdit("Organization Details")
                            }
                        />
                    </>
                ) : (
                    <>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "left",
                                alignItems: "center",
                                gap: "10px",
                                marginTop: "8px",
                                marginBottom: "8px",
                            }}
                        >
                            <ArrowBack
                                onClick={exitPermissionMode}
                                sx={{ fontSize: "20px", cursor: "pointer" }}
                            />
                            <Typography variant="subtitle1">
                                Organization Permissions
                            </Typography>

                            <div style={{ marginLeft: "auto" }}>
                                <AcxButton
                                    fullWidth
                                    color="primary"
                                    onClick={saveOrgPermissions}
                                >
                                    Save Permissions
                                </AcxButton>
                            </div>
                        </div>
                        <div
                            style={{
                                width: "100%",
                                background: "#FFFFFF",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    width: "100%",
                                    backgroundColor: theme.palette.blue.main,
                                }}
                            >
                                <Typography
                                    sx={{
                                        flex: "1 1 0px",
                                        margin: "auto",
                                        textAlign: "center",
                                        color: theme.palette.white.main,
                                        borderRight: "1px solid white",
                                    }}
                                    variant="subtitle2"
                                >
                                    Resources
                                </Typography>
                                <div
                                    style={{
                                        display: "flex",
                                        marginLeft: "auto",
                                        height: "100%",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            textAlign: "center",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                padding: "6px",
                                                color: theme.palette.white.main,
                                            }}
                                            variant="body2"
                                        >
                                            {organization?.name}
                                        </Typography>
                                        <div
                                            style={{
                                                display: "flex",
                                                flexDirection: "row",
                                                minWidth: "250px",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    flex: "1 1 0px",
                                                    padding: "0px 6px 6px 6px",
                                                    color: theme.palette.white
                                                        .main,
                                                }}
                                                variant="body1"
                                            >
                                                View
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    flex: "1 1 0px",
                                                    padding: "0px 6px 6px 6px",
                                                    color: theme.palette.white
                                                        .main,
                                                }}
                                                variant="body1"
                                            >
                                                Edit
                                            </Typography>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <ul
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    backgroundColor: "#FFFFFF",
                                    listStyle: "none",
                                    margin: "0px",
                                    padding: "0px",
                                    minWidth: "100%",
                                }}
                            >
                                {resources}
                            </ul>
                        </div>
                    </>
                )}
            </div>

            {confirm && (
                <AcxDialog
                    isOpen={!!confirm}
                    onClose={onClose}
                    title="Save organization"
                    text="Are you sure that you would like to commit these changes?"
                    subTitle="Authenticx recommends five tiers or less, if applicable."
                >
                    <AcxButton
                        onClick={registerSubmit(confirmSave, { onSuccess })}
                        color="primary"
                        loading={isLoading || isSubmitting}
                        disabled={isSubmitting}
                    >
                        Confirm
                    </AcxButton>
                </AcxDialog>
            )}
        </>
    );
};

export default OrganizationDetail;
